import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Field, useFormState } from 'react-final-form';
import {
  DateField,
  RadioField,
  SimpleSelectField,
} from '@unite-us/app-components';
import formatOptions from 'common/utils/formatOptions';

export const MILITARY_FORM_NAME = 'contact_military';

// Reusable component for conditional field sections
function ConditionalFieldWrapper({
  fieldName, fieldId, label, options, militaryEnums, showWhen,
}) {
  return (
    <div className={showWhen === 'yes' ? '' : 'hidden'}>
      <Field name={fieldName}>
        {({ input, meta }) => (
          <SimpleSelectField
            id={fieldId}
            label={label}
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums[options])}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={meta.touched && meta.error}
            menuPortalTarget={document.body}
          />
        )}
      </Field>
    </div>
  );
}

ConditionalFieldWrapper.propTypes = {
  fieldName: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.string.isRequired,
  militaryEnums: PropTypes.object.isRequired,
  showWhen: PropTypes.string,
};

ConditionalFieldWrapper.defaultProps = {
  showWhen: '',
};

// Reusable component for date range fields
function DateRangeFields({
  startFieldName, endFieldName, startId, endId, title,
}) {
  return (
    <div className="form-group">
      <h6>{title}</h6>
      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Field name={startFieldName}>
            {({ input, meta }) => (
              <DateField
                key={input.value || 'empty'}
                {...input}
                id={startId}
                label="Start Date"
                error={meta.touched && meta.error}
              />
            )}
          </Field>
        </div>
        <div className="col-xs-12 col-sm-6">
          <Field name={endFieldName}>
            {({ input, meta }) => (
              <DateField
                key={input.value || 'empty'}
                {...input}
                id={endId}
                label="End Date"
                error={meta.touched && meta.error}
              />
            )}
          </Field>
        </div>
      </div>
    </div>
  );
}

DateRangeFields.propTypes = {
  startFieldName: PropTypes.string.isRequired,
  endFieldName: PropTypes.string.isRequired,
  startId: PropTypes.string.isRequired,
  endId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

// Sub-component for deployment section
function DeploymentSection({ isDeployed }) {
  return (
    <div className={isDeployed === 'yes' ? '' : 'hidden'}>
      <DateRangeFields
        startFieldName="military.deployment_starts_at"
        endFieldName="military.deployment_ends_at"
        startId="deployment-dates-start"
        endId="deployment-dates-end"
        title="Deployment Dates"
      />
    </div>
  );
}

DeploymentSection.propTypes = {
  isDeployed: PropTypes.string,
};

DeploymentSection.defaultProps = {
  isDeployed: '',
};

// Sub-component for Service Status Fields
function ServiceStatusFields({ militaryEnums }) {
  return (
    <>
      <Field name="military.current_status">
        {({ input, meta }) => (
          <SimpleSelectField
            id="current_status"
            label="Current Status"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums.current_status)}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={meta.touched && meta.error}
            menuPortalTarget={document.body}
          />
        )}
      </Field>
      <div className="form-group">
        <div className="control-label">Currently Transitioning?</div>
        <Field name="military.currently_transitioning">
          {({ input, meta }) => (
            <RadioField
              id="transitioning"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={formatOptions(militaryEnums.transitioning)}
              error={meta.touched && meta.error}
            />
          )}
        </Field>
      </div>
      <div className="form-group">
        <div className="control-label">Has at least one day of active duty outside of basic training?</div>
        <Field name="military.at_least_one_day_active_duty">
          {({ input, meta }) => (
            <RadioField
              id="active-duty"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={formatOptions(militaryEnums.active_duty)}
              error={meta.touched && meta.error}
            />
          )}
        </Field>
      </div>
    </>
  );
}

ServiceStatusFields.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
};

// Sub-component for Branch and Era
function BranchEraFields({ militaryEnums }) {
  return (
    <>
      <Field name="military.branch">
        {({ input, meta }) => (
          <SimpleSelectField
            id="branch"
            label="Branch Of Service"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums.branch)}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={meta.touched && meta.error}
            menuPortalTarget={document.body}
          />
        )}
      </Field>
      <Field name="military.service_era">
        {({ input, meta }) => (
          <SimpleSelectField
            id="era"
            label="Service Era"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums.service_era)}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={meta.touched && meta.error}
            menuPortalTarget={document.body}
          />
        )}
      </Field>
    </>
  );
}

BranchEraFields.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
};

// Sub-component for Service Dates
function ServiceDates() {
  return (
    <DateRangeFields
      startFieldName="military.entry_date"
      endFieldName="military.exit_date"
      startId="service-dates-start"
      endId="service-dates-end"
      title="Service Dates"
    />
  );
}

// Reusable component for RadioField questions
function RadioFieldQuestion({
  fieldName, fieldId, label, options,
}) {
  return (
    <div className="form-group">
      <div className="control-label">{label}</div>
      <Field name={fieldName}>
        {({ input, meta }) => (
          <RadioField
            id={fieldId}
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={options}
            error={meta.touched && meta.error}
          />
        )}
      </Field>
    </div>
  );
}

RadioFieldQuestion.propTypes = {
  fieldName: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
};

// Sub-component for Discharge Fields
function DischargeFields({ militaryEnums }) {
  return (
    <>
      <Field name="military.discharge_type">
        {({ input, meta }) => (
          <SimpleSelectField
            id="discharge"
            label="Military Discharge"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            options={formatOptions(militaryEnums.discharge_type)}
            optionSettings={{ labelKey: 'label', valueKey: 'value' }}
            error={meta.touched && meta.error}
            menuPortalTarget={document.body}
          />
        )}
      </Field>
      <RadioFieldQuestion
        fieldName="military.discharged_due_to_disability"
        fieldId="discharge-disability"
        label="Discharged or retired from military service for a disability incurred in the line of duty?"
        options={formatOptions(militaryEnums.discharge_disability)}
      />
      <RadioFieldQuestion
        fieldName="military.service_connected_disability"
        fieldId="connected-disability"
        label="Has VA-rated Service-Connected Disability?"
        options={formatOptions(militaryEnums.connected_disability)}
      />
    </>
  );
}

DischargeFields.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
};

// Sub-component for military member details (orchestrator)
function MilitaryMemberDetails({ militaryEnums, values }) {
  return (
    <>
      <ServiceStatusFields militaryEnums={militaryEnums} />
      <BranchEraFields militaryEnums={militaryEnums} />
      <ServiceDates />
      <RadioFieldQuestion
        fieldName="military.deployed"
        fieldId="deployed"
        label="Has client ever been deployed?"
        options={formatOptions(militaryEnums.deployed)}
      />
      <DeploymentSection isDeployed={values.military?.deployed} />
      <DischargeFields militaryEnums={militaryEnums} />
      <ConditionalFieldWrapper
        fieldName="military.service_connected_disability_rating"
        fieldId="disablility-rating"
        label="Service-Connected Disability Rating"
        options="disability_rating"
        militaryEnums={militaryEnums}
        showWhen={values.military?.service_connected_disability}
      />
      <RadioFieldQuestion
        fieldName="military.proof_of_veteran_status"
        fieldId="proof"
        label="Proof of veteran status?"
        options={formatOptions(militaryEnums.proof_of_veteran_status)}
      />
      <ConditionalFieldWrapper
        fieldName="military.proof_type"
        fieldId="proof-type"
        label="Proof Type"
        options="proof_type"
        militaryEnums={militaryEnums}
        showWhen={values.military?.proof_of_veteran_status}
      />
    </>
  );
}

MilitaryMemberDetails.propTypes = {
  militaryEnums: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
};

export function MilitaryInformation({ enums }) {
  const { values } = useFormState({ subscription: { values: true } });

  const onAffiliationChange = () => {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 10);
  };

  const militaryEnums = _.wget(enums, 'military', {});

  return (
    <div className="contact-military-information">
      <h2 className="contact-military-information__header">Military Information</h2>
      <div className="contact-military-information__content">
        <div className="military-information">
          <Field name="military.affiliation">
            {({ input, meta }) => (
              <SimpleSelectField
                id="affiliation"
                label="Affiliation"
                value={input.value}
                onChange={(value) => {
                  input.onChange(value);
                  onAffiliationChange();
                }}
                onBlur={input.onBlur}
                options={formatOptions(militaryEnums.affiliation)}
                optionSettings={{ labelKey: 'label', valueKey: 'value' }}
                error={meta.touched && meta.error}
                menuPortalTarget={document.body}
              />
            )}
          </Field>
          <div className={values.military?.affiliation === 'military_member_or_veteran' ? '' : 'hidden'}>
            <MilitaryMemberDetails militaryEnums={militaryEnums} values={values} />
          </div>
        </div>
      </div>
    </div>
  );
}

MilitaryInformation.propTypes = {
  enums: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    enums: _.wget(state, 'session.enums.contact_data', {}),
  };
}

MilitaryInformation.fields = [
  'military.affiliation',
  'military.current_status',
  'military.currently_transitioning',
  'military.at_least_one_day_active_duty',
  'military.branch',
  'military.service_era',
  'military.entry_date',
  'military.exit_date',
  'military.deployed',
  'military.deployment_starts_at',
  'military.deployment_ends_at',
  'military.discharge_type',
  'military.discharged_due_to_disability',
  'military.service_connected_disability',
  'military.service_connected_disability_rating',
  'military.proof_of_veteran_status',
  'military.proof_type',
];

export default connect(mapStateToProps)(MilitaryInformation);
